/* RoosterPage — dienstplanning met wettelijke controles (BKR, opener/sluiter, diplom).
   Toont per dag alle shifts + compliance-status. Superadmin/locatiemanager only.
   Ondersteunt controles — geen wettelijke garantie. */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Clock, ExternalLink, Copy, CalendarRange, LayoutGrid, Thermometer, Sparkles, Send, Users, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { formatDate, localToday, localISO, localWeekMonday } from "@/lib/format";
import { Link } from "react-router-dom";
import ShiftLiveCheck from "./rooster/ShiftLiveCheck";
import ShiftWeekPreview from "./rooster/ShiftWeekPreview";
import { ChipYesNo } from "./rooster/ChipsCoverage";
import { FocusGauge } from "./rooster/FocusGauge";
import { SnelplannerSuggesties } from "./rooster/SnelplannerSuggesties";
import { VakantieVooruitblik } from "./rooster/VakantieVooruitblik";
import { KindDetailPopover, MedewerkerDetailPopover } from "./rooster/DetailPopovers";
import { BitcareWeekGrid } from "./rooster/BitcareWeekGrid";
import { PlanFocusWizard } from "./rooster/PlanFocusWizard";

export default function RoosterPage() {
  const [datum, setDatum] = useState(localToday());
  const [view, setView] = useState("day"); // "day" | "week" | "medewerker"
  const [shifts, setShifts] = useState([]);
  const [weekData, setWeekData] = useState(null);
  const [medewerkers, setMedewerkers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [focusGroupId, setFocusGroupId] = useState(() => {
    // WEBSTER KOV — Vaste Groep-Filter: onthoud laatste selectie via localStorage
    try { return localStorage.getItem("rooster.focusGroupId") || ""; } catch { return ""; }
  }); // WEBSTER KOV — per-groep plan-modus
  const [focusLocationId, setFocusLocationId] = useState(() => {
    try { return localStorage.getItem("rooster.focusLocationId") || ""; } catch { return ""; }
  }); // WEBSTER KOV — filter locatie (week+dag)
  const [weekBkr, setWeekBkr] = useState(null); // WEBSTER KOV — per-dag BKR strip voor weekview
  const [weekGrid, setWeekGrid] = useState(null); // WEBSTER KOV — Bitcare-style grid data
  const [dayGrid, setDayGrid] = useState(null); // WEBSTER KOV — dag-versie van dezelfde grid
  const [locations, setLocations] = useState([]);
  // Sorteer alfabetisch en filter inactieve locaties eruit — voorkomt dat verkeerd
  // geïmporteerde persoonsnamen in de "Kies locatie"-dropdown blijven staan.
  const activeLocations = useMemo(
    () => [...locations]
      .filter(l => l && l.active !== false)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl", { sensitivity: "base" })),
    [locations],
  );
  const [validation, setValidation] = useState(null);
  const [interval, setInterval] = useState(null); // {per_scope,...}
  const [urenWeek, setUrenWeek] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(newShiftForm());
  const [sickDialog, setSickDialog] = useState(null); // { shift, suggestions, bkr_breaks }
  const [aiDebug, setAiDebug] = useState(null); // { totaal, beschikbaar, toegewezen, details, assignedIds }
  const [cellAction, setCellAction] = useState(null); // { shift } — voor weekview-cel klik
  const [complianceReport, setComplianceReport] = useState(null); // {datum, gaps:[]}
  const [confirmPub, setConfirmPub] = useState(null); // { issues, body, label }
  const [loading, setLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null);     // live BKR/diploma resultaat voor de add-shift dialog
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [previewWeek, setPreviewWeek] = useState(null); // week grid voor de dialog (gefetcht bij openen)

  // Bereken maandag van huidige week — gebruikt LOCAL datum (geen UTC-shift).
  // localISO/localWeekMonday zijn nu geïmporteerd uit @/lib/format
  const weekMonday = localWeekMonday;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const commonPromises = [api.get("/medewerkers"), api.get("/groups"), api.get("/locations")];
      if (view === "week" || view === "medewerker") {
        const wk = weekMonday(datum);
        const bkrParams = {};
        if (focusLocationId) bkrParams.location_id = focusLocationId;
        if (focusGroupId) bkrParams.group_id = focusGroupId;
        const [w, m, g, l, u, wb, wg] = await Promise.all([
          api.get("/rooster/week", { params: { week_start: wk } }),
          ...commonPromises,
          api.get("/rooster/uren-week", { params: { week_start: wk } }),
          api.get("/planning/week-bkr", { params: { week_start: wk, ...bkrParams } }).catch(() => ({ data: null })),
          api.get("/planning/week-grid", { params: { week_start: wk, ...bkrParams } }).catch(() => ({ data: null })),
        ]);
        setWeekData(w.data);
        setUrenWeek(u.data);
        setWeekBkr(wb.data);
        setWeekGrid(wg.data);
        setShifts([]);
        setValidation(null); setInterval(null);
        setMedewerkers(m.data); setGroups(g.data); setLocations(l.data);
      } else {
        const gridParams = { week_start: datum, days: 1 };
        if (focusLocationId) gridParams.location_id = focusLocationId;
        if (focusGroupId) gridParams.group_id = focusGroupId;
        const [s, m, g, l, v, iv, dg] = await Promise.all([
          api.get("/rooster/day", { params: { datum } }),
          ...commonPromises,
          api.get("/rooster/validate", { params: { datum } }),
          api.get("/rooster/interval-check", { params: { datum } }),
          api.get("/planning/week-grid", { params: gridParams }).catch(() => ({ data: null })),
        ]);
        setShifts(s.data.shifts || []);
        setWeekData(null); setUrenWeek(null); setWeekGrid(null);
        setDayGrid(dg.data);
        setMedewerkers(m.data); setGroups(g.data); setLocations(l.data);
        setValidation(v.data);
        setInterval(iv.data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [datum, view, focusLocationId, focusGroupId]);

  useEffect(() => { load(); }, [load]);

  const save = async (newShifts) => {
    try {
      await api.post("/rooster/upsert", { datum, shifts: newShifts });
      toast.success("Rooster opgeslagen");
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  const addShift = async () => {
    if (!form.medewerker_id || !form.group_id) {
      toast.error("Kies medewerker en groep");
      return;
    }
    const g = groups.find(x => x.id === form.group_id);
    // Guarantee datum + location_id op de payload; ontbrekende datum leverde
    // eerder 422 'Field required' bij POST /rooster/upsert op.
    const patched = { ...form, datum, location_id: g?.location_id || form.location_id };
    const ok = await save([...shifts.map(cleanShift), cleanShift(patched)]);
    // Alleen sluiten/resetten als de save daadwerkelijk lukte, zodat de user
    // zijn form niet kwijt is bij een validatie-fout.
    if (ok) { setAddOpen(false); setForm(newShiftForm()); }
  };

  const removeShift = (id) => save(shifts.filter(s => s.id !== id).map(cleanShift));

  const downloadRoosterPdf = async (medId, weekStart, naam) => {
    try {
      const r = await api.get(`/rooster/pdf/medewerker/${medId}`, {
        params: { week_start: weekStart }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rooster_${(naam || "medewerker").replace(/\s+/g, "_")}_${weekStart}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF gedownload");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF-download mislukt");
    }
  };

  // Live dry-run (BKR + diploma + certificaten) tijdens invullen van 'Shift toevoegen'.
  // Debounced 250ms zodat rap typen niet elke keer een request afvuurt.
  useEffect(() => {
    if (!addOpen) return;
    if (!form.medewerker_id || !form.group_id || !form.start || !form.eind) {
      setDryRun(null);
      return;
    }
    const g = groups.find(x => x.id === form.group_id);
    const body = {
      datum,
      medewerker_id: form.medewerker_id,
      group_id: form.group_id,
      location_id: g?.location_id || form.location_id,
      start: form.start,
      eind: form.eind,
      is_opener: !!form.is_opener,
      is_sluiter: !!form.is_sluiter,
      pauze_start: form.pauze_start || null,
      pauze_eind: form.pauze_eind || null,
      geen_pauze: !!form.geen_pauze,
    };
    setDryRunBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/rooster/dry-run", body);
        setDryRun(r.data);
      } catch (e) {
        setDryRun({ error: formatApiError(e.response?.data?.detail) || "Live-check mislukt" });
      } finally {
        setDryRunBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [addOpen, form.medewerker_id, form.group_id, form.location_id, form.start, form.eind,
      form.is_opener, form.is_sluiter, form.pauze_start, form.pauze_eind, form.geen_pauze,
      datum, groups]);

  // Preview van huidige week (grid) — geladen bij openen van dialog voor visuele context.
  useEffect(() => {
    if (!addOpen) { setPreviewWeek(null); return; }
    const wk = weekMonday(datum);
    api.get("/rooster/week", { params: { week_start: wk } })
      .then(r => setPreviewWeek(r.data))
      .catch(() => setPreviewWeek(null));
  }, [addOpen, datum]);

  const sickShift = async (shift) => {
    if (!window.confirm(`${shift.medewerker_naam || "Deze medewerker"} ziekmelden voor deze shift?`)) return;
    try {
      const r = await api.post(`/rooster/${shift.id}/sick`, { reason: "Ziekmelding" });
      setSickDialog({ shift, ...r.data });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const acceptReplacement = async (replacement) => {
    if (!sickDialog) return;
    const orig = sickDialog.shift;
    try {
      const newShift = {
        datum: orig.datum, medewerker_id: replacement.id,
        group_id: orig.group_id, location_id: orig.location_id,
        start: orig.start, eind: orig.eind,
        is_opener: orig.is_opener, is_sluiter: orig.is_sluiter,
      };
      // Merge met huidige shifts (na sick zijn ze in load geladen)
      const current = await api.get("/rooster/day", { params: { datum: orig.datum } });
      const merged = [...(current.data.shifts || []).map(cleanShift), newShift];
      await api.post("/rooster/upsert", { datum: orig.datum, shifts: merged });
      toast.success(`${replacement.naam} ingepland als vervanger`);
      setSickDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const aiSuggest = async () => {
    if (shifts.length > 0 && !window.confirm(
      `Er zijn al ${shifts.length} shift(s) ingedeeld. AI-suggestie voegt voorstellen toe boven op de bestaande. Doorgaan?`
    )) return;
    try {
      const r = await api.post("/rooster/ai-suggest", { datum });
      const suggs = r.data.suggestions || [];
      const totaal = r.data.medewerkers_totaal ?? 0;
      const beschikbaar = r.data.medewerkers_beschikbaar ?? 0;
      if (suggs.length === 0) {
        // Specifieke uitleg op basis van waar de flow strandt
        if (totaal === 0) {
          toast.error("Geen medewerkers gevonden in je tenant — voeg eerst medewerkers toe.");
        } else if (beschikbaar === 0) {
          toast.info(
            `${totaal} medewerker(s) gevonden, maar geen enkele heeft beschikbaarheid ingevuld voor deze weekdag. ` +
            "Ga naar Medewerkers en vink de juiste weekdag aan met start/eind-tijd."
          );
        } else {
          toast.info(
            `${beschikbaar}/${totaal} medewerker(s) beschikbaar, maar er is geen BKR-vereiste groep om te vullen. ` +
            "Controleer of er kinderen gepland zijn (via contract of presentie)."
          );
        }
        return;
      }
      // Voeg toe aan bestaande shifts
      const newShifts = [
        ...shifts.map(cleanShift),
        ...suggs.map(s => ({
          datum, medewerker_id: s.medewerker_id,
          group_id: s.group_id || (groups.find(g => g.location_id === s.location_id)?.id) || "",
          location_id: s.location_id,
          start: s.start, eind: s.eind,
          pauze_start: s.pauze_start || null,
          pauze_eind: s.pauze_eind || null,
          is_opener: s.is_opener, is_sluiter: s.is_sluiter,
        })),
      ].filter(s => s.group_id);
      const ok = await save(newShifts);
      // Zet AI-uitleg zichtbaar: welke medewerkers zijn wel/niet meegenomen
      setAiDebug({
        totaal, beschikbaar, toegewezen: suggs.length,
        details: r.data.medewerkers_debug || [],
        assignedIds: new Set(suggs.map(s => s.medewerker_id)),
      });
      toast.success(`${suggs.length} AI-suggestie(s) toegevoegd — controleer en pas aan indien nodig`);
      // Compliance-nazicht: haal BKR-intervallen op en toon eventuele resterende tekorten
      if (ok) {
        try {
          const { data: iv } = await api.get(`/rooster/interval-check?datum=${datum}`);
          const gaps = [];
          for (const scope of (iv?.per_scope || [])) {
            for (const interval of (scope.intervals || [])) {
              if (!interval.ok) {
                const missing = Math.max(0, (interval.required || 0) - (interval.staff || 0));
                gaps.push({
                  scope: scope.name,
                  start: interval.start,
                  end: interval.eind,
                  required: interval.required,
                  staff: interval.staff,
                  missing,
                  issue: (interval.issues || []).join(" · "),
                });
              }
            }
          }
          setComplianceReport({ datum, gaps });
        } catch (e) { console.debug("Nazicht compliance-check faalde:", e); }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const aiSuggestWeek = async () => {
    const wk = weekMonday(datum);
    if (!window.confirm(
      `AI-rooster voor hele werkweek (ma-vr vanaf ${wk}). Bestaande shifts in de week worden vervangen. Doorgaan?`
    )) return;
    try {
      const r = await api.post("/rooster/ai-suggest-week", { week_start: wk, save: true });
      toast.success(`${r.data.totaal_shifts} shift(s) aangemaakt voor 5 werkdagen — controleer en pas aan indien nodig`);
      if (view !== "week") setView("week");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publiceer = async (bevestigd = false) => {
    // Guard: voorkom publiceren zonder shifts
    if (!bevestigd) {
      if (view === "day" && shifts.length === 0) {
        toast.error("Er zijn geen shifts voor deze dag om te publiceren.");
        return;
      }
      if ((view === "week" || view === "medewerker") &&
          (urenWeek?.medewerkers?.length ?? 0) === 0) {
        toast.error("Er zijn geen shifts voor deze week om te publiceren.");
        return;
      }
    }
    const body = view !== "day" ? { week_start: weekMonday(datum), bevestigd }
                                : { datum, bevestigd };
    const label = view !== "day" ? `week vanaf ${body.week_start}` : `dag ${datum}`;
    if (!bevestigd && !window.confirm(`Rooster publiceren en per e-mail versturen naar alle betrokken medewerkers voor ${label}?`)) return;
    try {
      const r = await api.post("/rooster/publiceer", body);
      // BKR-waarschuwing terug van server → toon bevestigings-dialoog
      if (r.data?.needs_confirmation) {
        setConfirmPub({ issues: r.data.bkr_issues || [], issue_count: r.data.issue_count || 0,
                        body, label });
        return;
      }
      const msg = r.data.email_key_configured
        ? `${r.data.sent} e-mail(s) verstuurd aan ${r.data.medewerkers} medewerker(s)${r.data.skipped_no_email ? ` · ${r.data.skipped_no_email} zonder e-mailadres overgeslagen` : ""}`
        : `In-app notificatie gestuurd naar ${r.data.medewerkers} medewerker(s) (e-mail-integratie niet geconfigureerd)`;
      toast.success(r.data.message || msg);
      setConfirmPub(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmPubliceer = async () => {
    if (!confirmPub) return;
    try {
      const r = await api.post("/rooster/publiceer", { ...confirmPub.body, bevestigd: true });
      const msg = r.data.email_key_configured
        ? `${r.data.sent} e-mail(s) verstuurd aan ${r.data.medewerkers} medewerker(s)`
        : `In-app notificatie gestuurd naar ${r.data.medewerkers} medewerker(s)`;
      toast.success(`Rooster gepubliceerd met BKR-waarschuwing · ${msg}`);
      setConfirmPub(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyLastWeek = async () => {
    const d = new Date(datum + "T00:00:00");
    d.setDate(d.getDate() - 7);
    const bron = localISO(d);  // FIX: local ISO ipv UTC toISOString (voorkomt off-by-1 dag)
    const weekday = d.toLocaleDateString('nl-NL', { weekday: 'long' });
    const bronLabel = `${weekday} ${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`;
    if (shifts.length > 0 && !window.confirm(
      `Alle ${shifts.length} shift(s) op deze dag worden vervangen door de shifts van ${bronLabel}. Doorgaan?`
    )) return;
    try {
      const r = await api.post("/rooster/copy", { datum, from_datum: bron });
      if (r.data.copied === 0) {
        toast.info(r.data.message || `Geen shifts gevonden op ${bronLabel}`);
      } else {
        toast.success(`${r.data.copied} shift(s) gekopieerd van ${bronLabel}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // WEBSTER KOV — persist focus-selectie in localStorage
  useEffect(() => {
    try {
      if (focusLocationId) localStorage.setItem("rooster.focusLocationId", focusLocationId);
      else localStorage.removeItem("rooster.focusLocationId");
    } catch { /* stil */ }
  }, [focusLocationId]);
  useEffect(() => {
    try {
      if (focusGroupId) localStorage.setItem("rooster.focusGroupId", focusGroupId);
      else localStorage.removeItem("rooster.focusGroupId");
    } catch { /* stil */ }
  }, [focusGroupId]);

  const grpMap = Object.fromEntries(groups.map(g => [g.id, g]));
  const locMap = Object.fromEntries(locations.map(l => [l.id, l.name]));

  // WEBSTER KOV — Focused planner mode: als locatie+groep gekozen zijn, filteren we
  // alle dag-widgets tot alleen die scope zodat de BKR strak bovenaan groen te
  // krijgen is per shift-toevoeging.
  const isFocused = view === "day" && !!focusGroupId;
  const focusGroup = focusGroupId ? grpMap[focusGroupId] : null;
  const focusLocation = focusLocationId ? locations.find(l => l.id === focusLocationId) : (focusGroup ? locations.find(l => l.id === focusGroup.location_id) : null);
  const filteredShifts = isFocused ? shifts.filter(s => s.group_id === focusGroupId) : shifts;

  return (
    <div>
      <PageHeader
        title="Roostering"
        subtitle="Dienstplanning met BKR-controle, opener/sluiter en gediplomeerde-op-groep."
        testid="rooster-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden" data-testid="view-toggle">
              <button
                onClick={() => setView("day")}
                data-testid="view-day"
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === "day" ? "bg-[color:var(--sage)] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              >Dag</button>
              <button
                onClick={() => setView("week")}
                data-testid="view-week"
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === "week" ? "bg-[color:var(--sage)] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              >Groep · Week</button>
              <button
                onClick={() => setView("medewerker")}
                data-testid="view-medewerker"
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === "medewerker" ? "bg-[color:var(--sage)] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              >Medewerker · Week</button>
            </div>
            <Input type="date" value={datum} onChange={e => setDatum(e.target.value)} className="w-40" data-testid="rooster-date" />
            {view === "day" && (
              <Button
                variant="outline"
                onClick={copyLastWeek}
                data-testid="copy-last-week-btn"
                className="border-[color:var(--sage)] text-[color:var(--sage)] hover:bg-[color:var(--sage)]/10"
                title="Kopieer alle shifts van dezelfde weekdag, 7 dagen eerder"
              >
                <Copy className="h-4 w-4 mr-1" /> Herhaal vorige week
              </Button>
            )}
            {view === "day" && (
              <Button
                variant="outline"
                onClick={aiSuggest}
                data-testid="ai-suggest-btn"
                className="border-[color:var(--terracotta)] text-[color:var(--terracotta)] hover:bg-[color:var(--terracotta)]/10"
                title="Genereer rooster-voorstel op basis van beschikbaarheid + BKR + diploma"
              >
                <Sparkles className="h-4 w-4 mr-1" /> AI-dag
              </Button>
            )}
            {(view === "week" || view === "medewerker") && (
              <Button
                variant="outline"
                onClick={aiSuggestWeek}
                data-testid="ai-week-btn"
                className="border-[color:var(--terracotta)] text-[color:var(--terracotta)] hover:bg-[color:var(--terracotta)]/10"
                title="AI-rooster voor hele werkweek (ma-vr) — vervangt bestaande shifts"
              >
                <Sparkles className="h-4 w-4 mr-1" /> AI-week
              </Button>
            )}
            <Button
              onClick={() => publiceer(false)}
              data-testid="publiceer-btn"
              style={{ background: "var(--sage)" }}
              className="text-white"
              title={view === "day"
                ? "Publiceer dag — verstuurt e-mail + in-app notificatie naar elke medewerker met eigen shifts"
                : "Publiceer hele week — verstuurt e-mail + in-app notificatie naar elke medewerker"}
            >
              <Send className="h-4 w-4 mr-1" />
              {view === "day" ? "Publiceer dag" : "Publiceer week"}
            </Button>
            {view === "day" && (
              <Button onClick={() => setAddOpen(true)} data-testid="add-shift-btn" style={{ background: "var(--sage)" }} className="text-white">
                <Plus className="h-4 w-4 mr-1" /> Shift toevoegen
              </Button>
            )}
          </div>
        }
      />

      {/* AI-uitleg uitklap-blok — verschijnt na klik op AI-dag zodat directeur
          begrijpt waarom bepaalde medewerkers wel/niet zijn meegenomen. */}
      {aiDebug && (
        <details open className="mb-4 rounded-md border border-purple-200 bg-purple-50" data-testid="ai-debug-block">
          <summary className="cursor-pointer px-4 py-2 text-sm font-semibold text-purple-900 flex items-center justify-between">
            <span>🤖 AI-uitleg — {aiDebug.toegewezen} toewijzingen · {aiDebug.beschikbaar}/{aiDebug.totaal} beschikbaar</span>
            <button
              className="text-xs text-purple-700 hover:underline"
              onClick={(e) => { e.preventDefault(); setAiDebug(null); }}
              data-testid="ai-debug-close">Sluiten</button>
          </summary>
          <div className="px-4 pb-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-purple-700 border-b border-purple-200">
                  <th className="text-left py-1.5 px-2">Medewerker</th>
                  <th className="text-left py-1.5 px-2">Status</th>
                  <th className="text-left py-1.5 px-2">Reden</th>
                </tr>
              </thead>
              <tbody>
                {aiDebug.details.map(m => {
                  const assigned = aiDebug.assignedIds.has(m.id);
                  return (
                    <tr key={m.id} className="border-b border-purple-100 last:border-0" data-testid={`ai-debug-row-${m.id}`}>
                      <td className="py-1 px-2 font-medium">{m.naam}</td>
                      <td className="py-1 px-2">
                        {assigned ? (
                          <span className="inline-block bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 rounded">✓ Ingedeeld</span>
                        ) : m.meegenomen ? (
                          <span className="inline-block bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded">Beschikbaar, niet nodig</span>
                        ) : (
                          <span className="inline-block bg-neutral-200 text-neutral-700 text-[10px] px-1.5 py-0.5 rounded">Niet meegenomen</span>
                        )}
                      </td>
                      <td className="py-1 px-2 text-neutral-600">{m.reden}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}

          Als beide gekozen zijn, filteren we shifts + validation naar die groep zodat
          de directeur ziet dat de BKR realtime van rood → oranje → groen kleurt terwijl
          er PMs worden ingeroosterd. */}
      {view === "day" && !loading && (
        <PlanFocusWizard
          locations={locations}
          groups={groups}
          focusLocationId={focusLocationId}
          focusGroupId={focusGroupId}
          onLocationChange={(id) => { setFocusLocationId(id); setFocusGroupId(""); }}
          onGroupChange={setFocusGroupId}
          datum={datum}
          refreshKey={shifts.length}
          onAddShift={(medewerkerId) => {
            // Prefill de shift-dialoog met gekozen locatie/groep (+ optionele PM).
            const loc_id = focusLocationId || (focusGroup ? focusGroup.location_id : "");
            setForm(f => ({
              ...f,
              location_id: loc_id,
              group_id: focusGroupId,
              medewerker_id: medewerkerId || f.medewerker_id,
            }));
            setAddOpen(true);
          }}
        />
      )}

      {/* WEBSTER KOV — Dag-view: zelfde Bitcare-grid als Groep·Week (user-request
          "bij dag planning moet dezelfde layout zijn als groep week"), maar met
          days=1. Alleen tonen als locatie+groep zijn gekozen. */}
      {view === "day" && !loading && focusLocationId && focusGroupId && dayGrid && dayGrid.locations && dayGrid.locations.length > 0 && (
        <Card className="p-4 bg-white shadow-none overflow-x-auto mb-4" data-testid="rooster-day-grid">
          <BitcareWeekGrid
            data={dayGrid}
            onCellClick={(s) => setCellAction({ shift: s })}
            onEmptyCellClick={(gridInfo) => {
              setDatum(gridInfo.datum);
              setForm(f => ({
                ...f,
                medewerker_id: gridInfo.medewerker_id,
                group_id: gridInfo.group_id,
                location_id: gridInfo.location_id,
                start: f.start || "08:00",
                eind: f.eind || "17:00",
              }));
              setAddOpen(true);
            }}
            onSuggestieAdd={({ medewerker_id, group_id, location_id, datum }) => {
              setDatum(datum);
              setForm(f => ({
                ...f,
                medewerker_id, group_id, location_id,
                start: f.start || "08:00",
                eind: f.eind || "17:00",
              }));
              setAddOpen(true);
            }}
            onGroupFocus={(gid) => {
              // Prompt 09 vervolg — deep-link vanuit BKR-reveal per-group
              setFocusGroupId(gid);
              const el = document.querySelector("[data-testid='wizard-add-shift']");
              if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
          />
        </Card>
      )}
      {loading && (
        <div className="mb-4 space-y-2" data-testid="rooster-loading">
          <div className="flex items-center gap-2 text-xs text-neutral-500 py-2 px-3 rounded-md bg-neutral-50 border border-neutral-200">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>
              {view === "day" ? "Dagrooster laden…" :
               view === "week" ? "Groep-weekoverzicht laden…" :
               "Medewerker-weekoverzicht laden…"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[0,1,2,3,4,5].map(i => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </div>
      )}

      {view === "day" && validation && (
        <>
          {/* WEBSTER KOV — Dag-vlag: één blik-status voor directie */}
          {(() => {
            const scopes = validation.per_scope || [];
            const kritiek = scopes.filter(s => !s.bkr_ok
                || (s.staff_count > 0 && !s.has_diploma));
            const waarschuwing = scopes.filter(s => s.staff_count > 0
                && s.bkr_ok
                && (!s.has_opener || !s.has_sluiter));
            const isOk = kritiek.length === 0 && waarschuwing.length === 0 && scopes.length > 0;
            const heeftShifts = scopes.some(s => s.staff_count > 0);
            const flagTone = kritiek.length ? "bg-red-50 border-red-200 text-red-900"
                          : waarschuwing.length ? "bg-amber-50 border-amber-200 text-amber-900"
                          : isOk ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                          : "bg-neutral-50 border-neutral-200 text-neutral-700";
            const iconEl = kritiek.length
              ? <AlertTriangle className="h-6 w-6 text-red-600 shrink-0" />
              : waarschuwing.length
              ? <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0" />
              : isOk
              ? <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
              : <AlertTriangle className="h-6 w-6 text-neutral-500 shrink-0" />;
            const flagLabel = kritiek.length
              ? `Publicatie geblokkeerd — ${kritiek.length} groep(en) niet compliant`
              : waarschuwing.length
              ? `Op sterkte, maar ${waarschuwing.length} groep(en) missen opener/sluiter`
              : isOk
              ? "Dag is compliant — klaar om te publiceren"
              : "Geen shifts gepland vandaag";
            return (
              <div className={`rounded-md border-2 p-4 mb-4 flex items-start gap-3 ${flagTone}`}
                   data-testid="rooster-day-flag">
                {iconEl}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-base">{flagLabel}</div>
                  {heeftShifts && (kritiek.length > 0 || waarschuwing.length > 0) && (
                    <div className="text-xs mt-1.5 space-y-0.5">
                      {kritiek.slice(0, 5).map(s => {
                        const missing = [];
                        if (!s.bkr_ok) missing.push(`BKR tekort ${s.required - s.staff_count}`);
                        if (s.staff_count > 0 && !s.has_diploma) missing.push("geen gediplomeerde PM");
                        return (
                          <div key={s.id} data-testid={`day-flag-kritiek-${s.id}`}>
                            <strong>{s.name}</strong> — {missing.join(" · ")}
                          </div>
                        );
                      })}
                      {waarschuwing.slice(0, 5).map(s => {
                        const miss = [];
                        if (!s.has_opener) miss.push("opener");
                        if (!s.has_sluiter) miss.push("sluiter");
                        return (
                          <div key={s.id} data-testid={`day-flag-waarschuwing-${s.id}`}>
                            <strong>{s.name}</strong> — mist {miss.join(" + ")}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 text-xs shrink-0">
                  <div className="flex items-center gap-1">
                    <span className="font-mono font-bold">{scopes.length - kritiek.length - waarschuwing.length}</span>
                    <span>compliant</span>
                  </div>
                  {waarschuwing.length > 0 && (
                    <div className="flex items-center gap-1 text-amber-700">
                      <span className="font-mono font-bold">{waarschuwing.length}</span>
                      <span>waarschuwing</span>
                    </div>
                  )}
                  {kritiek.length > 0 && (
                    <div className="flex items-center gap-1 text-red-700">
                      <span className="font-mono font-bold">{kritiek.length}</span>
                      <span>blokkerend</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

        <Card className="p-4 mb-6 bg-white shadow-none" data-testid="rooster-validation">
          <div className="flex items-center gap-2 mb-3">
            <LayoutGrid className="h-4 w-4 text-neutral-500" />
            <span className="font-medium text-sm text-neutral-700">Groep-overzicht (opener / sluiter / diploma)</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {validation.per_scope
              .filter(s => !isFocused || s.id === focusGroupId)
              .map(s => {
              const diplomaMissing = s.staff_count > 0 && !s.has_diploma;
              const openerSluiterMissing = s.staff_count > 0 && (!s.has_opener || !s.has_sluiter);
              const kritiek = !s.bkr_ok || diplomaMissing;
              const state = kritiek ? "onder"
                          : openerSluiterMissing ? "waarschuwing"
                          : (s.overbezet ? "over" : "ok");
              const tone = {
                onder: "border-red-300 bg-red-50",
                waarschuwing: "border-amber-300 bg-amber-50",
                over: "border-blue-300 bg-blue-50",
                ok: "border-emerald-200 bg-emerald-50/40",
              }[state];
              const label = { onder: "Blokkerend", waarschuwing: "Waarschuwing",
                              over: "Overbezet", ok: "Op sterkte" }[state];
              const labelTone = {
                onder: "bg-red-100 text-red-800",
                waarschuwing: "bg-amber-100 text-amber-800",
                over: "bg-blue-100 text-blue-800",
                ok: "bg-emerald-100 text-emerald-800",
              }[state];
              return (
                <div key={s.id} className={`p-3 rounded-md border ${tone}`} data-testid={`scope-${s.id}`}>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <div className="font-medium text-sm truncate">{s.name}</div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-neutral-50 text-neutral-600 border-neutral-200">
                        {s.type}
                      </Badge>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${labelTone}`} data-testid={`bkr-state-${s.id}`}>
                        {label}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-neutral-600 space-y-0.5">
                    <div>
                      Ingeroosterd: <strong>{s.staff_count}</strong> · vereist ≥ <strong>{s.required}</strong>
                      {s.surplus > 0 && s.bkr_ok && <span className="text-blue-700"> · surplus +{s.surplus}</span>}
                      {!s.bkr_ok && <span className="text-red-700"> · tekort {s.required - s.staff_count}</span>}
                    </div>
                    <div>Kinderen aanwezig: {s.kinderen}</div>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      <ChipYesNo ok={s.has_opener} label="Opener" />
                      <ChipYesNo ok={s.has_sluiter} label="Sluiter" />
                      <ChipYesNo ok={s.has_diploma} label="Gediplomeerd" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[11px] text-neutral-500 flex items-start gap-1.5">
            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
            {validation.disclaimer}{" "}
            <a href="https://www.1ratio.nl" target="_blank" rel="noopener noreferrer" className="text-[color:var(--sage)] underline inline-flex items-center gap-0.5">
              1ratio.nl <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </Card>
        </>
      )}

      {view === "day" && interval && interval.per_scope && interval.per_scope.length > 0 && (
        <Card className="p-4 mb-6 bg-white shadow-none" data-testid="rooster-interval-panel">
          <div className="flex items-center gap-2 mb-3">
            {interval.compliant
              ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> <span className="font-medium text-emerald-700 text-sm">Interval-BKR: elk moment op sterkte</span></>
              : <><AlertTriangle className="h-4 w-4 text-red-700" /> <span className="font-medium text-red-800 text-sm">Interval-BKR: overtredingen op één of meer momenten</span></>}
          </div>
          <div className="space-y-3">
            {interval.per_scope
              .filter(sc => !isFocused || sc.id === focusGroupId)
              .map(sc => (
              <div key={sc.id} data-testid={`interval-scope-${sc.id}`} className="border border-neutral-200 rounded-md p-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-sm font-medium">{sc.name} <span className="text-[10px] text-neutral-500 font-normal">· {sc.type}</span></div>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${sc.summary?.compliant ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                    {sc.summary?.compliant ? "OK" : `${Math.round((sc.summary?.violated_minutes || 0))} min tekort`}
                  </span>
                </div>
                <div className="space-y-1">
                  {(sc.intervals || []).map((iv, k) => (
                    <div key={`${iv.start}-${iv.eind}`} className={`text-[11px] rounded px-2 py-1 flex items-center justify-between gap-2 ${iv.ok ? "bg-emerald-50/50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid={`iv-${sc.id}-${k}`}>
                      <div className="font-mono shrink-0 w-24">{iv.start}–{iv.eind}</div>
                      <div className="flex-1 truncate">
                        <span className="font-semibold">{iv.kinderen}</span> kind · vereist ≥ <span className="font-semibold">{iv.required}</span> · aanwezig <span className="font-semibold">{iv.staff}</span>
                        {iv.staff_names?.length > 0 && <span className="text-neutral-500 ml-1">({iv.staff_names.slice(0, 3).join(", ")}{iv.staff_names.length > 3 ? "…" : ""})</span>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {iv.heeft_gediplomeerd ? <span title="Gediplomeerd" className="text-emerald-700">✓D</span> : <span title="Geen diploma" className="text-red-700">✗D</span>}
                        {iv.baby_needed && (iv.heeft_babytraining ? <span title="Babytraining" className="text-pink-700">✓B</span> : <span title="Babytraining vereist" className="text-red-700">✗B</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-neutral-500">
            Interval-berekening: openingstijden 07:30–18:30 default · pauze (60 min) van shifts &gt; 5,5 uur tellen niet mee.
          </div>
        </Card>
      )}

      {view === "day" && (
      <Card className="p-4 bg-white shadow-none">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
          Shifts ({filteredShifts.length}{isFocused ? ` · gefilterd op ${focusGroup?.name || ""}` : ""})
        </div>
        {filteredShifts.length === 0 && (
          <div className="py-10 px-6 text-center border-2 border-dashed border-neutral-200 rounded-md" data-testid="rooster-day-empty">
            <Clock className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
            <div className="text-sm font-medium text-neutral-700 mb-1">Nog geen shifts voor deze dag</div>
            <div className="text-xs text-neutral-500 mb-4 max-w-md mx-auto">
              {medewerkers.length === 0
                ? "Er zijn nog geen medewerkers geregistreerd. Voeg eerst medewerkers toe."
                : "Voeg handmatig een shift toe, kopieer de shifts van vorige week, of laat AI een dagvoorstel genereren."}
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {medewerkers.length === 0 ? (
                <Link to="/medewerkers">
                  <Button size="sm" variant="outline" data-testid="day-empty-medewerkers">
                    <Users className="h-3.5 w-3.5 mr-1" /> Ga naar Medewerkers
                  </Button>
                </Link>
              ) : (
                <>
                  <Button size="sm" onClick={() => setAddOpen(true)} style={{ background: "var(--sage)" }} className="text-white" data-testid="day-empty-add">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Shift toevoegen
                  </Button>
                  <Button size="sm" variant="outline" onClick={copyLastWeek} data-testid="day-empty-copy">
                    <Copy className="h-3.5 w-3.5 mr-1" /> Herhaal vorige week
                  </Button>
                  <Button size="sm" variant="outline" onClick={aiSuggest}
                    className="border-[color:var(--terracotta)] text-[color:var(--terracotta)] hover:bg-[color:var(--terracotta)]/10"
                    data-testid="day-empty-ai">
                    <Sparkles className="h-3.5 w-3.5 mr-1" /> AI-dag
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
        <div className="space-y-2">
          {filteredShifts.map(s => (
            <div key={s.id} className="flex items-center gap-3 py-2 border-b border-neutral-100 last:border-0" data-testid={`shift-${s.id}`}>
              <Clock className="h-4 w-4 text-neutral-400 shrink-0" />
              <div className="font-mono text-sm w-32 shrink-0">{s.start} — {s.eind}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.medewerker_naam}</div>
                <div className="text-xs text-neutral-500 truncate">{grpMap[s.group_id]?.name || "?"} · {locMap[s.location_id]}</div>
              </div>
              <div className="flex gap-1 flex-wrap">
                {s.is_opener && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Opener</Badge>}
                {s.is_sluiter && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Sluiter</Badge>}
                {s.gediplomeerd && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Diploma</Badge>}
                {s.heeft_ehbo && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">EHBO</Badge>}
                {s.heeft_babytraining && <Badge variant="outline" className="text-[10px] bg-pink-50 text-pink-700 border-pink-200">Baby</Badge>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => sickShift(s)}
                data-testid={`sick-shift-${s.id}`} className="text-amber-700 hover:bg-amber-50 shrink-0" title="Ziekmelden — toon vervangers">
                <Thermometer className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => removeShift(s.id)}
                data-testid={`remove-shift-${s.id}`} className="text-red-600 hover:bg-red-50 shrink-0">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
      )}

      {view === "week" && weekData && (
        <Card className="p-4 bg-white shadow-none overflow-x-auto" data-testid="rooster-week-grid">
          <VakantieVooruitblik weeks={12} onWeekClick={(iso) => setDatum(iso)} locationId={focusLocationId} />
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="text-xs font-semibold text-neutral-800 mb-0.5">Groep · Weekoverzicht</div>
              <div className="text-[11px] text-neutral-500">
                Week van {new Date(weekData.week_start + "T00:00:00").toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} · toont welke medewerkers welke groep bemannen
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={focusLocationId}
                onChange={(e) => { setFocusLocationId(e.target.value); setFocusGroupId(""); }}
                data-testid="week-filter-location"
                className="text-xs border rounded px-2 py-1 bg-white"
              >
                <option value="">Alle locaties</option>
                {activeLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <select
                value={focusGroupId}
                onChange={(e) => setFocusGroupId(e.target.value)}
                data-testid="week-filter-group"
                className="text-xs border rounded px-2 py-1 bg-white"
              >
                <option value="">Alle groepen</option>
                {groups
                  .filter(g => !focusLocationId || g.location_id === focusLocationId)
                  .map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              {(focusLocationId || focusGroupId) && (
                <button
                  onClick={() => { setFocusLocationId(""); setFocusGroupId(""); }}
                  className="text-[10px] text-neutral-500 underline hover:text-neutral-900"
                  data-testid="week-filter-reset"
                >
                  reset
                </button>
              )}
            </div>
          </div>

          {/* WEBSTER KOV — Live BKR-strip boven dagen-header. Toont per dag som van
              kinderen aanwezig, medewerkers ingeroosterd en aantal groepen onder BKR. */}
          {weekBkr && weekBkr.days && (() => {
            // WEBSTER KOV — user-request: verberg za/zo in BKR-live strip (KDV-default)
            const workdays = weekBkr.days.filter(day => {
              const wd = new Date(day.datum + "T00:00:00").getDay();
              return wd !== 0 && wd !== 6;
            });
            const gridColsClass = workdays.length === 5 ? "grid-cols-6" : "grid-cols-8";
            return (
            <div className="mb-3 border rounded-md bg-neutral-50 overflow-hidden" data-testid="week-bkr-strip">
              <div className={`grid ${gridColsClass} text-[10px]`}>
                <div className="px-2 py-1.5 bg-white font-semibold text-neutral-600 uppercase tracking-widest border-r border-neutral-200">
                  BKR live
                </div>
                {workdays.map(day => {
                  const totKids = day.bkr.reduce((n, b) => n + (b.kinderen || 0), 0);
                  const totStaff = day.bkr.reduce((n, b) => n + (b.medewerkers_ingeroosterd || 0), 0);
                  const totVereist = day.bkr.reduce((n, b) => n + (b.vereist || 0), 0);
                  const totAfw = day.bkr.reduce((n, b) => n + (b.kinderen_afwezig || 0), 0);
                  const alerts = day.bkr.filter(b => b.status === "alert").length;
                  const warns = day.bkr.filter(b => b.status === "warning").length;
                  const tone = totKids === 0 ? "bg-neutral-100 text-neutral-400"
                    : alerts ? "bg-red-50 text-red-900 border-l border-red-200"
                    : warns ? "bg-amber-50 text-amber-900 border-l border-amber-200"
                    : "bg-emerald-50 text-emerald-900 border-l border-emerald-200";
                  const dt = new Date(day.datum + "T00:00:00");
                  const isToday = localISO(dt) === localISO(new Date());
                  return (
                    <div key={day.datum}
                         className={`px-2 py-1.5 ${tone} ${isToday ? "ring-1 ring-inset ring-[color:var(--sage)]" : ""}`}
                         data-testid={`week-bkr-day-${day.datum}`}
                         title={`${day.datum}: ${totKids} kind${totAfw ? ` (${totAfw} afw.)` : ""} · ${totStaff}/${totVereist} pm${alerts ? ` · ${alerts} onder BKR` : warns ? ` · ${warns} krap` : ""}`}
                    >
                      {totKids === 0 ? (
                        <span className="opacity-60">—</span>
                      ) : (
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-semibold tabular-nums">{totStaff}/{totVereist}</span>
                          <span className="text-[9px] opacity-70">{totKids}k{totAfw ? `−${totAfw}` : ""}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}
          {weekData.rows.length === 0 && (
            <div className="py-10 px-6 text-center border-2 border-dashed border-neutral-200 rounded-md" data-testid="rooster-week-empty-scope">
              <LayoutGrid className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
              <div className="text-sm font-medium text-neutral-700 mb-1">Geen groepen/locaties in scope</div>
              <div className="text-xs text-neutral-500 mb-4 max-w-md mx-auto">
                Maak eerst een KDV-locatie met minimaal één groep aan onder Instellingen.
              </div>
              <Link to="/instellingen">
                <Button size="sm" variant="outline" data-testid="empty-cta-instellingen">Ga naar Instellingen</Button>
              </Link>
            </div>
          )}
          {weekData.rows.length > 0 && weekData.rows.every(r => Object.values(r.per_day || {}).every(cells => cells.length === 0)) && (
            <div className="py-6 px-4 text-center border-2 border-dashed border-neutral-200 rounded-md mb-3" data-testid="rooster-week-empty-shifts">
              <div className="text-sm font-medium text-neutral-700 mb-1">Nog geen shifts voor deze week</div>
              <div className="text-xs text-neutral-500 mb-3">
                Voeg per dag shifts toe (Dag-view) of laat AI een weekvoorstel maken.
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setView("day")} data-testid="week-empty-goto-day">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Shift toevoegen (Dag)
                </Button>
                <Button
                  size="sm"
                  onClick={aiSuggestWeek}
                  style={{ background: "var(--terracotta)" }}
                  className="text-white"
                  data-testid="week-empty-ai-week"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> AI-week genereren
                </Button>
              </div>
            </div>
          )}
          {/* WEBSTER KOV — Forced scope: alleen tonen wat de directeur selecteert.
              User-request: "laat alleen de locatie en groep zien die de directeur zelf
              selecteert". Toon een gate als er nog niets gekozen is. */}
          {(!focusLocationId || !focusGroupId) && (
            <div className="mb-3 rounded-md border-2 border-dashed border-amber-300 bg-amber-50/60 p-6 text-center"
                 data-testid="rooster-week-scope-gate">
              <div className="text-sm font-semibold text-amber-900 mb-2">
                Kies eerst een locatie én een groep om het rooster te tonen
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <select
                  value={focusLocationId}
                  onChange={(e) => { setFocusLocationId(e.target.value); setFocusGroupId(""); }}
                  className="text-sm border-2 rounded px-3 py-2 bg-white font-medium"
                  data-testid="scope-gate-locatie"
                >
                  <option value="">— kies locatie —</option>
                  {activeLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <select
                  value={focusGroupId}
                  onChange={(e) => setFocusGroupId(e.target.value)}
                  disabled={!focusLocationId}
                  className="text-sm border-2 rounded px-3 py-2 bg-white font-medium disabled:opacity-40"
                  data-testid="scope-gate-groep"
                >
                  <option value="">— kies groep —</option>
                  {groups
                    .filter(g => !focusLocationId || g.location_id === focusLocationId)
                    .map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            </div>
          )}
          {weekGrid && weekGrid.locations && weekGrid.locations.length > 0 && focusLocationId && focusGroupId && (
            <BitcareWeekGrid
              data={weekGrid}
              onCellClick={(s) => setCellAction({ shift: s })}
              onEmptyCellClick={(gridInfo) => {
                // WEBSTER KOV — Klik-om-toe-te-voegen: prefill shift-dialoog met
                // gekozen medewerker + groep + locatie + datum.
                setDatum(gridInfo.datum);
                setForm(f => ({
                  ...f,
                  medewerker_id: gridInfo.medewerker_id,
                  group_id: gridInfo.group_id,
                  location_id: gridInfo.location_id,
                  start: f.start || "08:00",
                  eind: f.eind || "17:00",
                }));
                setAddOpen(true);
              }}
              onCopyWeek={async ({ from_week_start, target_week_start }) => {
                // WEBSTER KOV — Bulk-kopieer met kiesbare doelweek + snapshot voor undo.
                try {
                  const payload = { week_start: target_week_start };
                  if (from_week_start) payload.from_week_start = from_week_start;
                  const r = await api.post("/rooster/copy-week", payload);
                  const { copied, undo_snapshot, undo_dates } = r.data || {};
                  const nxt = target_week_start;
                  setDatum(nxt);
                  // Undo-toast met 10 seconden countdown
                  toast.success(`${copied || 0} shifts gekopieerd naar ${nxt}`, {
                    duration: 10000,
                    action: {
                      label: "Ongedaan maken",
                      onClick: async () => {
                        try {
                          await api.post("/rooster/restore-shifts", {
                            dates: undo_dates,
                            shifts: undo_snapshot,
                          });
                          toast.success("Kopie-actie ongedaan gemaakt");
                          setWeekGrid(null); // trigger re-fetch
                          // Reload via datum-refresh
                          setDatum(d => d + "");
                        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      },
                    },
                  });
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              onSuggestieAdd={({ medewerker_id, group_id, location_id, datum }) => {
                // WEBSTER KOV — Snelplanner in Grid: prefill shift-dialoog met suggestie
                setDatum(datum);
                setForm(f => ({
                  ...f,
                  medewerker_id,
                  group_id,
                  location_id,
                  start: f.start || "08:00",
                  eind: f.eind || "17:00",
                }));
                setAddOpen(true);
              }}
              onKindOverride={async (payload) => {
                // WEBSTER KOV — Kind-slepen: verhuis voor ALLE geplande shift-dagen
                // deze week zodat directie niet 5x moet slepen. Fallback: één datum.
                const datums = payload.datums && payload.datums.length ? payload.datums : [payload.datum];
                try {
                  for (const d of datums) {
                    await api.post("/planning/verhuis", {
                      kind_id: payload.kind_id,
                      naar_group_id: payload.new_group_id,
                      mode: "tijdelijk",
                      datum: d,
                      dagdelen: ["ochtend", "middag"],
                    });
                  }
                  const wk = weekGrid.week_start;
                  const wg = await api.get("/planning/week-grid", { params: { week_start: wk, ...(focusLocationId ? { location_id: focusLocationId } : {}), ...(focusGroupId ? { group_id: focusGroupId } : {}) } });
                  setWeekGrid(wg.data);
                  const dagenLabel = datums.length === 1 ? datums[0] : `${datums.length} dagen`;
                  const promoteToPermanent = async () => {
                    try {
                      await api.post("/planning/verhuis", {
                        kind_id: payload.kind_id,
                        naar_group_id: payload.new_group_id,
                        mode: "permanent",
                        reason: `Drag-drop promotie naar permanent (${dagenLabel})`,
                      });
                      toast.success(`Contract mee-verplaatst naar ${payload.new_group_naam}`);
                      const wg3 = await api.get("/planning/week-grid", { params: { week_start: wk, ...(focusLocationId ? { location_id: focusLocationId } : {}), ...(focusGroupId ? { group_id: focusGroupId } : {}) } });
                      setWeekGrid(wg3.data);
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Contract-verplaatsing mislukt"); }
                  };
                  const undoTijdelijk = async () => {
                    try {
                      for (const d of datums) {
                        await api.post("/planning/verhuis", {
                          kind_id: payload.kind_id,
                          naar_group_id: payload.from_group_id,
                          mode: "tijdelijk",
                          datum: d,
                          dagdelen: ["ochtend", "middag"],
                        });
                      }
                      const wg2 = await api.get("/planning/week-grid", { params: { week_start: wk, ...(focusLocationId ? { location_id: focusLocationId } : {}), ...(focusGroupId ? { group_id: focusGroupId } : {}) } });
                      setWeekGrid(wg2.data);
                      toast.success("Verplaatsing ongedaan gemaakt");
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  };
                  toast.custom((t) => (
                    <div className="bg-white border border-emerald-200 rounded-md shadow-lg p-3 max-w-sm" data-testid="kind-verhuis-toast">
                      <div className="text-sm font-medium text-emerald-900">
                        {payload.kind_naam} verplaatst naar {payload.new_group_naam}
                      </div>
                      <div className="text-xs text-neutral-600 mt-0.5">
                        Tijdelijk voor {dagenLabel}. Contract nog op oude groep.
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          className="text-xs bg-teal-600 hover:bg-teal-700 text-white px-2 py-1 rounded"
                          data-testid="kind-verhuis-permanent-btn"
                          onClick={() => { toast.dismiss(t); promoteToPermanent(); }}
                        >
                          Ook contract mee-verplaatsen
                        </button>
                        <button
                          className="text-xs border border-neutral-300 hover:bg-neutral-50 px-2 py-1 rounded"
                          data-testid="kind-verhuis-undo-btn"
                          onClick={() => { toast.dismiss(t); undoTijdelijk(); }}
                        >
                          Ongedaan maken
                        </button>
                      </div>
                    </div>
                  ), { duration: 12000 });
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              onGroupFocus={(gid) => {
                setFocusGroupId(gid);
              }}
            />
          )}
          {weekGrid && weekGrid.locations && weekGrid.locations.length === 0 && (
            <div className="py-6 px-4 text-center border-2 border-dashed border-neutral-200 rounded-md" data-testid="rooster-week-empty-shifts">
              <div className="text-sm font-medium text-neutral-700 mb-1">Geen locaties of groepen om te tonen</div>
              <div className="text-xs text-neutral-500 mb-3">
                Selecteer een andere locatie/groep of voeg groepen toe.
              </div>
            </div>
          )}
          <div className="mt-3 text-[10px] text-neutral-500 flex flex-wrap gap-3">
            <span><strong>Groen</strong>=BKR compliant · <strong>Amber</strong>=krap · <strong>Rood</strong>=onder BKR · Klik op een shift-blok voor acties.</span>
          </div>
        </Card>
      )}

      {view === "medewerker" && urenWeek && (
        <Card className="p-4 bg-white shadow-none overflow-x-auto" data-testid="rooster-medewerker-week">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="text-xs font-semibold text-neutral-800 mb-0.5">Medewerker · Weekoverzicht</div>
              <div className="text-[11px] text-neutral-500">
                Week van {new Date(urenWeek.week_start + "T00:00:00").toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} · toont uren per medewerker
              </div>
            </div>
            <div className="text-xs text-neutral-600 shrink-0">
              Totaal betaalde uren: <strong className="text-neutral-900">{urenWeek.totaal_uren}</strong>
            </div>
          </div>
          {urenWeek.medewerkers.length === 0 && (
            <div className="py-10 px-6 text-center border-2 border-dashed border-neutral-200 rounded-md" data-testid="rooster-medewerker-empty">
              <Users className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
              <div className="text-sm font-medium text-neutral-700 mb-1">Nog geen shifts voor deze week</div>
              <div className="text-xs text-neutral-500 mb-4 max-w-md mx-auto">
                {medewerkers.length === 0
                  ? "Er zijn nog geen medewerkers geregistreerd. Voeg eerst medewerkers toe voordat je een rooster kan maken."
                  : "Wijs medewerkers toe aan dagen via 'Shift toevoegen' in de Dag-view, of laat AI-week een voorstel genereren voor ma–vr."}
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {medewerkers.length === 0 ? (
                  <Link to="/medewerkers">
                    <Button size="sm" variant="outline" data-testid="empty-cta-medewerkers">
                      <Users className="h-3.5 w-3.5 mr-1" /> Ga naar Medewerkers
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setView("day")} data-testid="empty-cta-day">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Shift toevoegen (Dag)
                    </Button>
                    <Button
                      size="sm"
                      onClick={aiSuggestWeek}
                      style={{ background: "var(--terracotta)" }}
                      className="text-white"
                      data-testid="empty-cta-ai-week"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> AI-week genereren
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
          {urenWeek.medewerkers.length > 0 && (
            <table className="w-full text-xs border-collapse min-w-[900px]">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className="text-left py-2 pr-3 sticky left-0 bg-white font-semibold text-neutral-600 uppercase text-[10px] tracking-widest">Medewerker</th>
                  {urenWeek.days.map(d => {
                    const dt = new Date(d + "T00:00:00");
                    const wkNames = ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"];
                    return (
                      <th key={d} className="text-left py-2 px-2 font-semibold uppercase text-[10px] tracking-widest text-neutral-600">
                        {wkNames[dt.getDay()]}<br />
                        <span className="text-[9px] normal-case font-normal">{dt.getDate()}/{dt.getMonth() + 1}</span>
                      </th>
                    );
                  })}
                  <th className="text-right py-2 pl-3 pr-1 font-semibold uppercase text-[10px] tracking-widest text-neutral-600">Uren</th>
                </tr>
              </thead>
              <tbody>
                {urenWeek.medewerkers.map(mw => {
                  const perDay = {};
                  (mw.shifts || []).forEach(s => {
                    (perDay[s.datum] = perDay[s.datum] || []).push(s);
                  });
                  return (
                    <tr key={mw.medewerker_id} className="border-b border-neutral-100 hover:bg-neutral-50/50" data-testid={`med-row-${mw.medewerker_id}`}>
                      <td className="py-2 pr-3 sticky left-0 bg-white align-top">
                        <div className="flex items-center gap-1.5">
                          <div className="text-sm font-medium">{mw.naam}</div>
                          <button
                            type="button"
                            onClick={() => downloadRoosterPdf(mw.medewerker_id, urenWeek.week_start, mw.naam)}
                            className="text-neutral-400 hover:text-[color:var(--sage)] transition-colors"
                            title="Download PDF weekrooster voor deze medewerker"
                            data-testid={`med-pdf-${mw.medewerker_id}`}
                          >
                            <FileDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="text-[10px] text-neutral-500">{Math.round((mw.total_break_minutes || 0) / 60 * 100) / 100}u pauze</div>
                      </td>
                      {urenWeek.days.map(d => {
                        const cells = perDay[d] || [];
                        return (
                          <td key={d} className="py-1 px-1 align-top min-w-[100px]">
                            {cells.length === 0 && <span className="text-neutral-300 text-[10px]">—</span>}
                            <div className="space-y-1">
                              {cells.map(c => (
                                <div key={c.id} className="text-[10px] rounded px-1.5 py-1 bg-neutral-50 border border-neutral-200">
                                  <div className="font-mono">{c.start}-{c.eind}</div>
                                  {c.pauze && <div className="text-[9px] text-amber-700">pauze {c.pauze}</div>}
                                  <div className="text-[9px] text-neutral-500">{(c.betaalde_minuten / 60).toFixed(2)}u betaald</div>
                                </div>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                      <td className="text-right py-2 pl-3 pr-1 align-top">
                        <div className="text-sm font-semibold text-neutral-800">
                          {(mw.total_paid_minutes / 60).toFixed(2)}u
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="mt-3 text-[10px] text-neutral-500">
            Betaalde uren = shift-duur minus 60 minuten pauze bij shifts &gt; 5,5 uur.
          </div>
        </Card>
      )}

      {/* Compliance-nazicht na AI-dag */}
      <Dialog open={!!complianceReport} onOpenChange={(v) => { if (!v) setComplianceReport(null); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="ai-compliance-dialog">
          <DialogHeader>
            <DialogTitle>Compliance-nazicht</DialogTitle>
            <DialogDescription>
              Na AI-dag: overzicht van resterende BKR-tekorten voor {complianceReport?.datum}
            </DialogDescription>
          </DialogHeader>
          {complianceReport && complianceReport.gaps.length === 0 ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-800" data-testid="ai-compliance-ok">
              ✓ Alle tijdvakken voldoen aan BKR. Geen actie nodig.
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="ai-compliance-gaps">
              {(complianceReport?.gaps || []).map((g, idx) => (
                <div key={`${g.scope}-${g.start}-${g.end}-${idx}`} className="p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                  <div className="font-medium text-amber-900">
                    {g.scope} · {g.start}–{g.end}
                  </div>
                  <div className="text-amber-800 text-xs mt-0.5">
                    {g.staff} van {g.required} PM aanwezig — hier ontbreekt nog <span className="font-semibold">{g.missing}</span> medewerker{g.missing !== 1 ? 's' : ''}.
                  </div>
                  {g.issue && <div className="text-[11px] text-amber-700/80 mt-1">{g.issue}</div>}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setComplianceReport(null)} data-testid="ai-compliance-close">Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="add-shift-dialog">
          <DialogHeader>
            <DialogTitle>Nieuwe shift · {new Date(datum + "T00:00:00").toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</DialogTitle>
            <DialogDescription>
              Wijs een medewerker toe. Rechts zie je live wat het effect is op BKR, diploma en certificaten.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Kolom links: het formulier */}
            <div className="lg:col-span-2 grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Medewerker</Label>
                <Select value={form.medewerker_id} onValueChange={v => setForm({ ...form, medewerker_id: v })}>
                  <SelectTrigger data-testid="shift-medewerker"><SelectValue placeholder="Kies medewerker" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {medewerkers.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.voornaam} {m.achternaam}
                        {m.gediplomeerd === false ? " · geen diploma" : ""}
                        {m.heeft_ehbo ? " · EHBO" : ""}
                        {m.heeft_babytraining ? " · Baby" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Groep (KDV) of Locatie (BSO)</Label>
                <Select value={form.group_id} onValueChange={v => {
                  const g = groups.find(x => x.id === v);
                  setForm({ ...form, group_id: v, location_id: g?.location_id || form.location_id });
                }}>
                  <SelectTrigger data-testid="shift-group"><SelectValue placeholder="Kies groep" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name} — {locMap[g.location_id]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Starttijd</Label><Input type="time" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} data-testid="shift-start" /></div>
              <div><Label>Eindtijd</Label><Input type="time" value={form.eind} onChange={e => setForm({ ...form, eind: e.target.value })} data-testid="shift-eind" /></div>
              <div><Label>Pauze start <span className="text-neutral-400 text-[10px]">(optioneel)</span></Label>
                <Input type="time" value={form.pauze_start || ""} onChange={e => setForm({ ...form, pauze_start: e.target.value })} data-testid="shift-pauze-start" disabled={form.geen_pauze} /></div>
              <div><Label>Pauze eind</Label>
                <Input type="time" value={form.pauze_eind || ""} onChange={e => setForm({ ...form, pauze_eind: e.target.value })} data-testid="shift-pauze-eind" disabled={form.geen_pauze} /></div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.is_opener} onCheckedChange={c => setForm({ ...form, is_opener: !!c })} data-testid="shift-opener" /> Opener
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.is_sluiter} onCheckedChange={c => setForm({ ...form, is_sluiter: !!c })} data-testid="shift-sluiter" /> Sluiter
              </label>
              <label className="col-span-2 flex items-center gap-2 text-xs text-neutral-600">
                <Checkbox checked={form.geen_pauze} onCheckedChange={c => setForm({ ...form, geen_pauze: !!c, pauze_start: "", pauze_eind: "" })} data-testid="shift-geen-pauze" />
                Geen pauze (opt-out automatische 60 min pauze bij shift &gt; 5,5 uur)
              </label>
            </div>

            {/* Kolom rechts: live BKR + week preview */}
            <div className="lg:col-span-3 space-y-3">
              <ShiftLiveCheck dryRun={dryRun} busy={dryRunBusy} form={form} />
              <ShiftWeekPreview
                weekData={previewWeek}
                selectedDate={datum}
                selectedGroupId={form.group_id}
                selectedMedewerkerId={form.medewerker_id}
                form={form}
                grpMap={grpMap}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Annuleren</Button>
            <Button onClick={addShift} data-testid="shift-save" style={{ background: "var(--sage)" }} className="text-white">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cell-action popup (week-view) */}
      <Dialog open={!!cellAction} onOpenChange={(v) => !v && setCellAction(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="cell-action-dialog">
          <DialogHeader>
            <DialogTitle>Shift-acties</DialogTitle>
            <DialogDescription>
              {cellAction?.shift.medewerker_naam} · {cellAction?.shift.start}—{cellAction?.shift.eind}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                const sh = cellAction.shift;
                setCellAction(null);
                setDatum(weekData.days.find(d => (weekData.rows.find(r => r.per_day[d]?.some(c => c.id === sh.id)))));
                setView("day");
                toast.info("Ga naar dag-view om te bewerken");
              }}
              className="justify-start"
              data-testid="cell-action-goto-day"
            >
              <CalendarRange className="h-4 w-4 mr-2" /> Open in dag-view
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const sh = cellAction.shift;
                setCellAction(null);
                try {
                  const r = await api.post(`/rooster/${sh.id}/sick`, { reason: "Ziekmelding" });
                  setSickDialog({ shift: sh, ...r.data });
                  load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              className="justify-start text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="cell-action-sick"
            >
              <Thermometer className="h-4 w-4 mr-2" /> Ziekmelden + vervanger zoeken
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const sh = cellAction.shift;
                setCellAction(null);
                if (!window.confirm(`Verwijder shift ${sh.medewerker_naam} ${sh.start}-${sh.eind}?`)) return;
                try {
                  await api.delete(`/rooster/${sh.id}`);
                  toast.success("Shift verwijderd");
                  load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              className="justify-start text-red-700 border-red-300 hover:bg-red-50"
              data-testid="cell-action-delete"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Verwijderen
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCellAction(null)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BKR-waarschuwing bij publiceren — WEBSTER KOV: categoriseer issues */}
      <Dialog open={!!confirmPub} onOpenChange={(v) => !v && setConfirmPub(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="publiceer-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
              Publicatie vereist bevestiging
            </DialogTitle>
            <DialogDescription>
              Op {confirmPub?.label} zijn <strong>{confirmPub?.issue_count}</strong> waarschuwing(en) gedetecteerd.
              Publiceren gaat alsnog door — elke waarschuwing wordt in het audit-log opgenomen met jouw naam.
            </DialogDescription>
          </DialogHeader>

          {(() => {
            const issues = confirmPub?.issues || [];
            const buckets = {
              opener: issues.filter(m => /opener/i.test(m)),
              sluiter: issues.filter(m => /sluiter/i.test(m)),
              diploma: issues.filter(m => /gediplomeerde|diploma/i.test(m)),
              bkr: issues.filter(m => /bkr|tekort|pm ingezet/i.test(m)),
              overig: issues.filter(m => !/opener|sluiter|diploma|bkr|tekort|pm ingezet/i.test(m)),
            };
            const bucketMeta = [
              { key: "opener",  label: "Opener ontbreekt",   tone: "bg-amber-50 border-amber-200 text-amber-900" },
              { key: "sluiter", label: "Sluiter ontbreekt",  tone: "bg-amber-50 border-amber-200 text-amber-900" },
              { key: "diploma", label: "Geen gediplomeerde PM", tone: "bg-red-50 border-red-200 text-red-900" },
              { key: "bkr",     label: "BKR-tekort",          tone: "bg-red-50 border-red-200 text-red-900" },
              { key: "overig",  label: "Overige",             tone: "bg-neutral-50 border-neutral-200 text-neutral-800" },
            ];
            return (
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="pub-issue-buckets">
                {bucketMeta.map(m => {
                  const list = buckets[m.key];
                  if (!list.length) return null;
                  return (
                    <div key={m.key} className={`border rounded p-2 ${m.tone}`}
                         data-testid={`pub-bucket-${m.key}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-widest mb-1">
                        {m.label} · {list.length}
                      </div>
                      <div className="space-y-0.5">
                        {list.slice(0, 8).map((msg, k) => (
                          <div key={`${m.key}-${msg}`} className="text-[11px]" data-testid={`pub-issue-${m.key}-${k}`}>
                            {msg}
                          </div>
                        ))}
                        {list.length > 8 && (
                          <div className="text-[10px] italic opacity-70">
                            …+ {list.length - 8} overige
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {confirmPub && (confirmPub.issue_count || 0) > issues.length && (
                  <div className="text-neutral-500 italic text-xs">
                    …+ {confirmPub.issue_count - issues.length} niet getoond (max 50 in respons)
                  </div>
                )}
              </div>
            );
          })()}

          <div className="text-[11px] text-neutral-500 mt-1">
            Definitieve toetsing via <a href="https://www.1ratio.nl" target="_blank" rel="noopener noreferrer" className="text-[color:var(--sage)] underline">1ratio.nl</a> (SZW).
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmPub(null)} data-testid="pub-cancel">Annuleren</Button>
            <Button onClick={confirmPubliceer} data-testid="pub-confirm" style={{ background: "var(--terracotta)" }} className="text-white">
              Toch publiceren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Sick / suggested-fix dialog */}
      <Dialog open={!!sickDialog} onOpenChange={(v) => !v && setSickDialog(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="sick-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Thermometer className="h-5 w-5 text-amber-700" />
              Ziekmelding — vervanger kiezen
            </DialogTitle>
            <DialogDescription>
              {sickDialog?.shift.medewerker_naam} is uitgevallen. Kies een beschikbare vervanger of sla over.
            </DialogDescription>
          </DialogHeader>
          {sickDialog?.bkr_breaks && (
            <div className="p-2.5 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div><strong>BKR-alert:</strong> zonder vervanger wordt niet meer voldaan aan de vereiste beroepskracht-kindratio of gediplomeerde-op-groep.</div>
            </div>
          )}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {(!sickDialog?.suggested_replacements || sickDialog.suggested_replacements.length === 0) && (
              <div className="text-sm text-neutral-500 py-4 text-center">Geen beschikbare vervangers op deze locatie.</div>
            )}
            {sickDialog?.suggested_replacements?.map(r => (
              <div key={r.id} className="flex items-center gap-2 p-2 rounded-md border border-neutral-200 hover:bg-neutral-50" data-testid={`repl-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.naam}</div>
                  <div className="text-[11px] text-neutral-500 flex gap-2 flex-wrap">
                    <span>{r.functie || "medewerker"}</span>
                    {r.gediplomeerd && <span className="text-emerald-700">✓ diploma</span>}
                    {!r.gediplomeerd && <span className="text-red-700">✗ geen diploma</span>}
                  </div>
                </div>
                <Button size="sm" onClick={() => acceptReplacement(r)} data-testid={`accept-repl-${r.id}`}
                  style={{ background: "var(--sage)" }} className="text-white text-xs">
                  Inplannen
                </Button>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-neutral-500 mt-2">{sickDialog?.disclaimer}</div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSickDialog(null)}>Sluiten zonder vervanger</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function newShiftForm() {
  return { medewerker_id: "", group_id: "", location_id: "", start: "08:00", eind: "17:00",
           is_opener: false, is_sluiter: false,
           pauze_start: "", pauze_eind: "", geen_pauze: false };
}

function cleanShift(s) {
  return {
    id: s.id, datum: s.datum, medewerker_id: s.medewerker_id,
    group_id: s.group_id, location_id: s.location_id,
    start: s.start, eind: s.eind,
    is_opener: !!s.is_opener, is_sluiter: !!s.is_sluiter,
    pauze_start: s.pauze_start || null,
    pauze_eind: s.pauze_eind || null,
    geen_pauze: !!s.geen_pauze,
  };
}
















